/**
 * The board face's focus mechanics — `FE-13.4`, discharging the two edge cases
 * of `FR-EXP-022` that are behaviour rather than colour, plus the scroll half
 * of `FR-VIEW-010`. `BoardFace` (agent B) is the only caller.
 *
 * The colour half of `FE-13.4` is measured in `board-contrast.test.ts`, which
 * has no runtime counterpart on purpose: the ring is `styles.css`'s
 * `:focus-visible` rule and nothing here paints it.
 *
 * ── What is deliberately NOT here ──────────────────────────────────────────
 *  - **Focus trapping and focus return** (`FR-EXP-022` AC2/AC3) belong to the
 *    primitives from `FE-04.10`. `Dialog`, `Popover`, `Menu`, `Combobox` and
 *    `Tooltip` are Radix, which traps and restores focus itself; re-implementing
 *    it here would fight them.
 *  - **`FR-EXP-023`** — the product-wide reduced-motion policy — is a separate
 *    requirement and a separate block. `prefersReducedMotion` exists only so
 *    that the scrolling *this* file initiates does not animate against a member
 *    who asked for no motion. It is not that requirement's implementation.
 *  - **j/k card selection**, named in `FR-VIEW-010`'s behaviour text, is `B41`'s
 *    (keyboard shortcuts). What C1 owes `FR-EXP-022` is Tab reachability and a
 *    visible ring, nothing more.
 *  - **Edge-scroll while dragging** (`FR-VIEW-010` AC3) is `FE-12.3`'s.
 */

/**
 * Has the member asked their operating system for reduced motion?
 *
 * Read at call time rather than subscribed to, because every caller here is
 * already inside an event handler — a member who changes the setting mid-session
 * gets the new answer on the next keystroke, and nothing has to be torn down.
 *
 * The two guards are not defensive noise. `typeof window` covers a `node`
 * environment (both sibling contrast suites declare one), and the `matchMedia`
 * guard covers a DOM that has no media-query support at all — which is also the
 * safe answer, since "no preference expressed" is what `false` means.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * `FR-EXP-022`'s first edge case: *"horizontally scrolled lists must scroll into
 * view when a card inside them receives focus."*
 *
 * A card on the board face sits inside **two** scrollers — its column scrolls
 * vertically and the strip scrolls horizontally — so both axes have to move, and
 * `scrollIntoView` walks every scrollable ancestor in one call, which is why
 * this is not two `scrollLeft`/`scrollTop` sums.
 *
 * `block: 'nearest', inline: 'nearest'` is the load-bearing part. The defaults
 * (`block: 'start'`) re-centre every scroller on the path, including the
 * document, so tabbing to an already-visible card visibly yanks the page.
 * `nearest` scrolls the minimum distance and does nothing at all when the
 * element is already in view.
 *
 * `null` is the ordinary case, not an error: `BoardFace` passes whatever the
 * focus event's target resolved to, and focus leaving the board entirely gives
 * nothing to reveal.
 */
export function revealFocusedElement(
  element: HTMLElement | null,
  options: { readonly reducedMotion?: boolean | undefined } = {},
): void {
  if (element === null) return;
  // jsdom has no layout engine and stubs this method; an older DOM may not have
  // it at all. Either way there is nothing to scroll, and throwing from a focus
  // handler would take the board down.
  if (typeof element.scrollIntoView !== 'function') return;

  element.scrollIntoView({
    block: 'nearest',
    inline: 'nearest',
    behavior:
      options.reducedMotion === true || prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

/**
 * How much horizontal scroll **this application** should add to a wheel event
 * over the list strip — `FR-VIEW-010` AC1.
 *
 * ⚠ **The browser already does the common case, and doing it twice is a real
 * bug that only appears in a real browser.** What actually happens:
 *
 *  - A **trackpad** produces a genuine two-axis gesture: `deltaX` is already
 *    populated and `overflow-x: auto` on the strip consumes it. Nothing to add.
 *  - **Shift + wheel** is translated *below* the page on every mainstream
 *    combination — macOS swaps the axes in the window server, and Chrome and
 *    Firefox swap them on Windows and Linux — so the event that reaches
 *    JavaScript **already carries the delta on `deltaX`**. Adding `deltaY` on
 *    top of it scrolls twice as far as the member asked for, and a unit test
 *    cannot see that, because a synthetic `WheelEvent` carries whatever the test
 *    puts in it.
 *  - A **plain vertical wheel with no shift** must be left alone. Over the board
 *    it belongs to the column the pointer is in — turning it into horizontal
 *    scroll would make a 200-card column unreachable with a mouse.
 *
 * So the honest scope of this function is **one case**: shift is held and the
 * platform did *not* swap the axes, leaving `deltaX` at zero. There it returns
 * `deltaY`; everywhere else it returns `0`, meaning *"leave the event alone —
 * do not call `preventDefault`."* A caller that scrolls on a zero return, or
 * that calls `preventDefault` unconditionally, reintroduces the doubling this
 * function exists to avoid.
 *
 * The return is in the event's own units. `WheelEvent.deltaMode` is not part of
 * the fixed signature, so a `DOM_DELTA_LINE` or `DOM_DELTA_PAGE` event returns
 * lines or pages; scaling that to pixels is the caller's, and today's caller
 * only ever meets `DOM_DELTA_PIXEL`.
 */
export function wheelToHorizontalDelta(
  event: Pick<WheelEvent, 'deltaX' | 'deltaY' | 'shiftKey'>,
): number {
  // The platform (or the pointing device) already produced a horizontal delta.
  // Native overflow scrolling will consume it; anything added here is doubled.
  if (event.deltaX !== 0) return 0;

  // A vertical wheel with no shift belongs to whichever column the pointer is
  // over. Only an explicit shift asks for the other axis.
  if (!event.shiftKey) return 0;

  return event.deltaY;
}
